import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import type { EventMediaSettingsDto, EventMediaSettingsPatchDto } from '@/types/dtos/media/media-settings.dto';
import MediaSettingsSwitchRow from '@/components/media/media-settings-switch-row';
import {
  type MediaSettingsConfirmKey,
  mediaSettingsConfirmMessageKey,
} from '@/components/media/media-settings-confirm.types';
import { usePatchEventMediaSettingsMutation } from '@/state/mutations/events/use-patch-event-media-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { getObjectId } from '@/helpers/object.id.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import EventVirtualMediaSettingsSection from '@/components/media/event-virtual-media-settings-section';

const defaultMediaSettings = (): EventMediaSettingsDto => ({
  hosts: { allowSelfCreate: true, allowReorder: true, allowFlowOverride: true },
  speakers: { allowFlowOverride: true, allowSessionOverride: true },
  virtualMedia: {
    allowFlowOverride: true,
    allowSessionOverride: true,
    flowScopes: {
      pre_flow: { enabledSlugs: ['event-info'] },
      flow_intro: { enabledSlugs: ['flow-info'] },
      flow_transition: { enabledSlugs: ['flow-info'] },
      flow_resume: { enabledSlugs: ['flow-info'] },
      flow_outro: { enabledSlugs: ['flow-info'] },
      after_flow: { enabledSlugs: ['event-info'] },
    },
    slotByContentType: {
      Break: { enabledSlugs: ['break-info'] },
    },
  },
});

type PendingFieldId =
  | 'hosts.allowSelfCreate'
  | 'hosts.allowReorder'
  | 'hosts.allowFlowOverride'
  | 'speakers.allowFlowOverride'
  | 'speakers.allowSessionOverride'
  | 'virtualMedia.allowFlowOverride'
  | 'virtualMedia.allowSessionOverride';

type PendingChange = {
  fieldId: PendingFieldId;
  patch: EventMediaSettingsPatchDto;
  confirmKey: MediaSettingsConfirmKey;
};

const HOST_FIELD_CONFIRM: Record<'allowSelfCreate' | 'allowReorder' | 'allowFlowOverride', MediaSettingsConfirmKey> = {
  allowSelfCreate: 'hosts.allowSelfCreate.off',
  allowReorder: 'hosts.allowReorder.off',
  allowFlowOverride: 'hosts.allowFlowOverride.off',
};

const SPEAKER_FIELD_CONFIRM: Record<'allowFlowOverride' | 'allowSessionOverride', MediaSettingsConfirmKey> = {
  allowFlowOverride: 'speakers.allowFlowOverride.off',
  allowSessionOverride: 'speakers.allowSessionOverride.off',
};

export default function EventMediaSettingsForm({
  eventId,
  initialSettings,
}: {
  eventId: string;
  initialSettings?: GetEventSettingsDto;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const originEventId = event?.origin ? getObjectId(event.origin) : eventId;

  const [draft, setDraft] = useState<EventMediaSettingsDto>(initialSettings?.mediaSettings ?? defaultMediaSettings());
  const [pending, setPending] = useState<PendingChange | null>(null);

  const canEdit = initialSettings?.mediaSettingsCapabilities?.canEdit ?? false;

  useEffect(() => {
    if (initialSettings?.mediaSettings) {
      setDraft(initialSettings.mediaSettings);
    }
  }, [initialSettings?.mediaSettings]);

  const { mutateAsync, isPending } = usePatchEventMediaSettingsMutation();

  const mergeDraft = (prev: EventMediaSettingsDto, patch: EventMediaSettingsPatchDto): EventMediaSettingsDto => {
    const prevVirtual = prev.virtualMedia ?? defaultMediaSettings().virtualMedia!;
    const nextVirtual = patch.virtualMedia
      ? {
          allowFlowOverride: patch.virtualMedia.allowFlowOverride ?? prevVirtual.allowFlowOverride,
          allowSessionOverride: patch.virtualMedia.allowSessionOverride ?? prevVirtual.allowSessionOverride,
          flowScopes: patch.virtualMedia.flowScopes
            ? { ...prevVirtual.flowScopes, ...patch.virtualMedia.flowScopes }
            : prevVirtual.flowScopes,
          slotByContentType: patch.virtualMedia.slotByContentType
            ? { ...prevVirtual.slotByContentType, ...patch.virtualMedia.slotByContentType }
            : prevVirtual.slotByContentType,
        }
      : prevVirtual;
    return {
      hosts: { ...prev.hosts, ...patch.hosts },
      speakers: { ...prev.speakers, ...patch.speakers },
      virtualMedia: nextVirtual,
    };
  };

  const applyPatch = async (patch: EventMediaSettingsPatchDto) => {
    try {
      const result = await mutateAsync({ eventId: originEventId, mediaSettings: patch });
      if (result?.mediaSettings) {
        setDraft(result.mediaSettings);
      } else {
        setDraft((prev) => mergeDraft(prev, patch));
      }
      notification.notifySuccess(t('media.settings.saveSuccess'));
    } catch {
      notification.notifyError(t('media.settings.saveError'));
    }
  };

  const cancelPending = () => {
    if (pending) {
      setDraft((prev) => {
        const revertPatch: EventMediaSettingsPatchDto = {};
        if (pending.fieldId === 'hosts.allowSelfCreate') {
          revertPatch.hosts = { allowSelfCreate: !prev.hosts.allowSelfCreate };
        } else if (pending.fieldId === 'hosts.allowReorder') {
          revertPatch.hosts = { allowReorder: !prev.hosts.allowReorder };
        } else if (pending.fieldId === 'hosts.allowFlowOverride') {
          revertPatch.hosts = { allowFlowOverride: !prev.hosts.allowFlowOverride };
        } else if (pending.fieldId === 'speakers.allowFlowOverride') {
          revertPatch.speakers = { allowFlowOverride: !prev.speakers.allowFlowOverride };
        } else if (pending.fieldId === 'speakers.allowSessionOverride') {
          revertPatch.speakers = { allowSessionOverride: !prev.speakers.allowSessionOverride };
        } else if (pending.fieldId === 'virtualMedia.allowFlowOverride') {
          revertPatch.virtualMedia = { allowFlowOverride: !(prev.virtualMedia?.allowFlowOverride ?? true) };
        } else if (pending.fieldId === 'virtualMedia.allowSessionOverride') {
          revertPatch.virtualMedia = { allowSessionOverride: !(prev.virtualMedia?.allowSessionOverride ?? true) };
        }
        return mergeDraft(prev, revertPatch);
      });
    }
    setPending(null);
  };

  const confirmPending = () => {
    const change = pending;
    setPending(null);
    if (change) {
      void applyPatch(change.patch);
    }
  };

  const switchConfirm = (fieldId: PendingFieldId) => {
    if (pending?.fieldId !== fieldId) return null;
    return {
      message: t(mediaSettingsConfirmMessageKey(pending.confirmKey)),
      onCancel: cancelPending,
      onConfirm: confirmPending,
    };
  };

  const requestChange = (
    fieldId: PendingFieldId,
    patch: EventMediaSettingsPatchDto,
    confirmKey?: MediaSettingsConfirmKey,
  ) => {
    if (confirmKey) {
      setDraft((prev) => mergeDraft(prev, patch));
      setPending({ fieldId, patch, confirmKey });
      return;
    }
    void applyPatch(patch);
  };

  const setHostField = (field: 'allowSelfCreate' | 'allowReorder' | 'allowFlowOverride', value: boolean) => {
    const fieldId = `hosts.${field}` as PendingFieldId;
    const confirmKey = !value ? HOST_FIELD_CONFIRM[field] : undefined;
    const patch: EventMediaSettingsPatchDto =
      field === 'allowFlowOverride'
        ? { hosts: { allowFlowOverride: value } }
        : field === 'allowSelfCreate'
          ? { hosts: { allowSelfCreate: value } }
          : { hosts: { allowReorder: value } };
    requestChange(fieldId, patch, confirmKey);
  };

  const setSpeakerField = (field: 'allowFlowOverride' | 'allowSessionOverride', value: boolean) => {
    const fieldId = `speakers.${field}` as PendingFieldId;
    const confirmKey = !value ? SPEAKER_FIELD_CONFIRM[field] : undefined;
    const patch: EventMediaSettingsPatchDto =
      field === 'allowFlowOverride'
        ? { speakers: { allowFlowOverride: value } }
        : { speakers: { allowSessionOverride: value } };
    requestChange(fieldId, patch, confirmKey);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pr: 1 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.media.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('event.detail.settingsMenu.media.description')}
      </Typography>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('event.detail.settingsMenu.media.hostsSection')}
          </Typography>
          <MediaSettingsSwitchRow
            switchKey={`event-host-allowSelfCreate-${draft.hosts.allowSelfCreate}`}
            label={t('media.settings.hosts.allowSelfCreate')}
            checked={draft.hosts.allowSelfCreate}
            disabled={!canEdit || isPending}
            confirm={switchConfirm('hosts.allowSelfCreate')}
            onChange={(v) => setHostField('allowSelfCreate', v)}
          />
          <MediaSettingsSwitchRow
            switchKey={`event-host-allowReorder-${draft.hosts.allowReorder}`}
            label={t('media.settings.hosts.allowReorder')}
            checked={draft.hosts.allowReorder}
            disabled={!canEdit || isPending}
            confirm={switchConfirm('hosts.allowReorder')}
            onChange={(v) => setHostField('allowReorder', v)}
          />
          <MediaSettingsSwitchRow
            switchKey={`event-host-allowFlowOverride-${draft.hosts.allowFlowOverride}`}
            label={t('media.settings.hosts.allowFlowOverride')}
            checked={draft.hosts.allowFlowOverride}
            disabled={!canEdit || isPending}
            confirm={switchConfirm('hosts.allowFlowOverride')}
            onChange={(v) => setHostField('allowFlowOverride', v)}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('event.detail.settingsMenu.media.speakersSection')}
          </Typography>
          <MediaSettingsSwitchRow
            switchKey={`event-speaker-allowFlowOverride-${draft.speakers.allowFlowOverride}`}
            label={t('media.settings.speakers.allowFlowOverride')}
            checked={draft.speakers.allowFlowOverride}
            disabled={!canEdit || isPending}
            confirm={switchConfirm('speakers.allowFlowOverride')}
            onChange={(v) => setSpeakerField('allowFlowOverride', v)}
          />
          <MediaSettingsSwitchRow
            switchKey={`event-speaker-allowSessionOverride-${draft.speakers.allowSessionOverride}`}
            label={t('media.settings.speakers.allowSessionOverride')}
            checked={draft.speakers.allowSessionOverride}
            disabled={!canEdit || isPending}
            confirm={switchConfirm('speakers.allowSessionOverride')}
            onChange={(v) => setSpeakerField('allowSessionOverride', v)}
          />
        </Box>
        <EventVirtualMediaSettingsSection
          draft={draft}
          canEdit={canEdit}
          isPending={isPending}
          virtualFlowOverrideConfirm={switchConfirm('virtualMedia.allowFlowOverride')}
          virtualSessionOverrideConfirm={switchConfirm('virtualMedia.allowSessionOverride')}
          onRequestChange={(patch, confirmKey, fieldId) => {
            if (confirmKey && fieldId) {
              requestChange(fieldId, patch, confirmKey);
              return;
            }
            void applyPatch(patch);
          }}
        />
      </Stack>
    </Box>
  );
}
